import { type Constraint, type ConstraintId } from '@/domain/constraints';
import { parentsOf } from '@/domain/construction';
import { type Vec2 } from '@/domain/math';
import {
  addConstraint,
  addEntity,
  dependentsOf,
  type Entity,
  type EntityId,
  removeConstraint,
  removeEntity,
  type SceneDocument,
  type Style,
  updateEntity,
  withStyle,
} from '@/domain/model';
import { type Euler, type ProjectionKind } from '@/domain/solid';

/**
 * Edit intents.
 *
 * Tools never touch the document; they describe what they want in these terms and the
 * application layer applies it. That indirection is what M8 turns into undo: a command is
 * an action plus the inverse patch computed when it was applied, and history is a log of
 * those. Building tools that mutate directly and retrofitting undo afterwards is the
 * usual way editors end up with actions that cannot be undone.
 *
 * Every action is plain serialisable data, which is also what makes replay and
 * (eventually) collaboration possible over the same mechanism.
 */
export type EditAction =
  | { readonly kind: 'add-entity'; readonly entity: Entity }
  | { readonly kind: 'update-entity'; readonly entity: Entity }
  | { readonly kind: 'remove-entity'; readonly id: EntityId }
  | { readonly kind: 'add-constraint'; readonly constraint: Constraint }
  | { readonly kind: 'remove-constraint'; readonly id: ConstraintId }
  /**
   * Restyles entities in place.
   *
   * A dedicated action rather than a series of `update-entity`s, for two reasons: the
   * caller does not have to reconstruct whole entity records just to change a colour,
   * and the undo entry can say "Change colour" instead of "Edit".
   *
   * `style` is a partial, so "make these dashed" leaves every other property alone —
   * which is what the panel needs when several entities of different colours are
   * selected.
   */
  | {
      readonly kind: 'set-style';
      readonly ids: readonly EntityId[];
      readonly style: Partial<Style>;
    }
  /**
   * Solid parameters: size, tessellation, orientation, projection.
   *
   * Separate from `set-style` because these are *construction* parameters, not appearance:
   * changing the projection changes which edges are hidden, which is geometry. Ids that do
   * not name a solid are skipped rather than rejected, so the panel can address a mixed
   * selection without filtering first.
   */
  | {
      readonly kind: 'set-solid';
      readonly ids: readonly EntityId[];
      readonly patch: {
        readonly width?: number;
        readonly height?: number;
        readonly depth?: number;
        readonly sides?: number;
        readonly taper?: number;
        readonly rotation?: Euler;
        readonly projection?: ProjectionKind;
      };
    }
  /**
   * Angle-mark options: how many arcs, and whether the value is written.
   *
   * Its own action rather than a general "edit construction parameters", because these two
   * are the only ones a user adjusts after the fact and a general mechanism would have to
   * validate every field of every op to stay safe.
   */
  | {
      readonly kind: 'set-angle-mark';
      readonly ids: readonly EntityId[];
      readonly ticks?: number | undefined;
      readonly showValue?: boolean | undefined;
      /**
       * Arc size, as a multiple of the ordinary one.
       *
       * Listed here because it was once *not*, and the omission was invisible: the controller
       * built its action by spreading a patch object, and a spread carries no excess-property
       * check, so `size` was passed, type-checked, dispatched and silently dropped by the
       * reducer below. The panel's "to" and "rất to" buttons did nothing at all, and nothing
       * anywhere reported a problem.
       */
      readonly size?: number | undefined;
      /** Where the user dragged the measure to, relative to where it would sit by itself. */
      readonly valueOffset?: Vec2 | undefined;
    }
  /**
   * Where along its segment a point sits, as a fraction.
   *
   * Two constructions place a point on a segment and they arrive by different routes. A
   * `locus-point` is one the user *dragged* onto a host and positioned by eye; a `ratio-point`
   * is one that was *stated* — "AM = 2/3 AB" — and never had an approximate stage. Both store
   * the same number and both are edited here, which is what makes "I dragged it roughly, now
   * say exactly what it is" a single act rather than a delete and a retype.
   *
   * A locus point on anything other than a segment is left alone. `t` runs 0…1 along a circle
   * or an arc too, but there it is an angle in disguise and a fraction is the wrong way to
   * think about it — a panel offering thirds of a circle would be offering something the user
   * did not ask for.
   */
  | {
      readonly kind: 'set-ratio';
      readonly ids: readonly EntityId[];
      readonly t: number;
    }
  /**
   * How far a turned plane leans off the one it was turned from.
   *
   * Its own action rather than a field on `set-solid`, because it belongs to the *plane* and not
   * to the body: two planes cut into one solid may lean by different amounts, and a change to one
   * must not touch the other.
   *
   * `reverse` is a rotation of the sign rather than a second number, and that is deliberate. The
   * two answers to "sixty degrees off this plane" are `+60` and `-60`, so a reverse is exactly a
   * negation — which means the button and the typed number can never disagree about which plane
   * is on screen. Onshape's equivalent left that relationship unstated and its users have been
   * unable to establish it for nine years.
   */
  | {
      readonly kind: 'set-plane-turn';
      readonly ids: readonly EntityId[];
      readonly degrees?: number | undefined;
      readonly reverse?: boolean | undefined;
    }
  /**
   * A blackboard plane's lean, and how much of it is drawn.
   *
   * `tilt` is geometry: it decides the angle between this plane and every other. `size` is
   * appearance, because a plane goes on forever and the parallelogram is only where the author
   * stopped drawing it — no angle, foot or intersection changes with it. Both in one action
   * because one panel edits them, and splitting them would cost two undo steps for one adjustment.
   */
  | {
      readonly kind: 'set-plane-free';
      readonly ids: readonly EntityId[];
      readonly tilt?: number | undefined;
      readonly size?: number | undefined;
    }
  /**
   * Presentation flags: visibility, lock, draw order, label.
   *
   * Grouped into one action rather than four because the layers panel toggles them the
   * same way and they share an undo story. Every field is optional, so "hide these" does
   * not disturb their lock state.
   *
   * `label` uses `null` to mean *remove the label*, because `undefined` is
   * indistinguishable from "not mentioned" under `exactOptionalPropertyTypes`.
   */
  | {
      readonly kind: 'set-presentation';
      readonly ids: readonly EntityId[];
      readonly visible?: boolean | undefined;
      readonly locked?: boolean | undefined;
      readonly zIndex?: number | undefined;
      readonly label?: string | null | undefined;
      /** Marks the entity as scaffolding, which "hide construction" acts on. */
      readonly isConstruction?: boolean | undefined;
    };

export interface ApplyResult {
  readonly document: SceneDocument;
  /**
   * Entities to seed incremental re-evaluation with.
   *
   * Returned rather than recomputed because only the applier knows exactly what it
   * touched — deriving it afterwards by diffing two documents would cost more than the
   * evaluation it is meant to narrow.
   */
  readonly seeds: readonly EntityId[];
  /** Entities removed, including those that cascaded. */
  readonly removed: readonly EntityId[];
}

export function applyActions(document: SceneDocument, actions: readonly EditAction[]): ApplyResult {
  let next = document;
  const seeds: EntityId[] = [];
  const removed: EntityId[] = [];

  for (const action of actions) {
    switch (action.kind) {
      case 'add-entity':
        next = addEntity(next, action.entity);
        seeds.push(action.entity.id);
        break;

      case 'update-entity':
        // Adding rather than updating a missing entity would let a tool silently
        // resurrect something the user deleted.
        next = updateEntity(next, action.entity);
        seeds.push(action.entity.id);
        break;

      case 'remove-entity': {
        const doomed = next.entities.get(action.id);
        const result = removeEntity(next, action.id);
        next = result.document;
        removed.push(...result.removed);

        /*
         * A queue, not a single pass — scaffolding can rest on scaffolding.
         *
         * A blackboard plane hangs off an invisible viewpoint, and the viewpoint hangs off an
         * invisible anchor. Sweeping one level left the anchor behind: unreachable, undeletable
         * except through the object list, and one per figure the user ever drew. Following each
         * removal's own parents costs a queue and stops at the first thing that is visible or
         * still supports something.
         */
        const doomedQueue = [doomed];
        while (doomedQueue.length > 0) {
          const gone = doomedQueue.shift();
          for (const id of unreachableParentsOf(next, gone)) {
            const orphan = next.entities.get(id);
            const swept = removeEntity(next, id);
            next = swept.document;
            removed.push(...swept.removed);
            doomedQueue.push(orphan);
          }
        }
        break;
      }

      case 'add-constraint':
        next = addConstraint(next, action.constraint);
        break;

      case 'remove-constraint':
        next = removeConstraint(next, action.id);
        break;

      case 'set-style':
        for (const id of action.ids) {
          const entity = next.entities.get(id);
          if (entity === undefined) continue;
          const restyled = withStyle(entity.style, action.style);
          next = updateEntity(next, { ...entity, style: restyled });
          // Style changes nothing geometric, but the entity record changed, so history
          // and the renderer both need to know.
          seeds.push(id);
        }
        break;

      case 'set-angle-mark':
        for (const id of action.ids) {
          const entity = next.entities.get(id);
          // A mixed selection is normal; anything else simply has no arcs to change.
          if (entity?.source.op !== 'angle-mark') continue;

          next = updateEntity(next, {
            ...entity,
            source: {
              ...entity.source,
              ...(action.ticks === undefined
                ? {}
                : { ticks: Math.max(1, Math.min(3, Math.round(action.ticks))) }),
              ...(action.showValue === undefined ? {} : { showValue: action.showValue }),
              // The same range evaluation clamps to, applied here so the stored document never
              // holds a size the renderer would quietly ignore.
              ...(action.size === undefined
                ? {}
                : { size: Math.max(0.5, Math.min(4, action.size)) }),
              ...(action.valueOffset === undefined ? {} : { valueOffset: action.valueOffset }),
            },
          });
          seeds.push(id);
        }
        break;

      case 'set-ratio':
        for (const id of action.ids) {
          const entity = next.entities.get(id);
          if (entity === undefined) continue;

          const { source } = entity;
          const divisible =
            source.op === 'ratio-point' ||
            (source.op === 'locus-point' &&
              next.entities.get(source.host)?.source.op === 'segment');
          // A mixed selection is normal; anything else has no position along a segment.
          if (!divisible) continue;

          next = updateEntity(next, { ...entity, source: { ...source, t: action.t } });
          seeds.push(id);
        }
        break;

      case 'set-plane-free':
        for (const id of action.ids) {
          const entity = next.entities.get(id);
          // A mixed selection is normal; a section of a solid has no tilt of its own to set.
          if (entity?.source.op !== 'plane-free') continue;

          const tilt = action.tilt ?? entity.source.tilt;
          const size = action.size ?? entity.source.size;
          if (!Number.isFinite(tilt) || !(size > 0)) continue;

          next = updateEntity(next, { ...entity, source: { ...entity.source, tilt, size } });
          seeds.push(id);
        }
        break;

      case 'set-plane-turn':
        for (const id of action.ids) {
          const entity = next.entities.get(id);
          // A mixed selection is normal; an ordinary plane has no angle to change.
          if (entity?.source.op !== 'plane-turn') continue;

          const asked = action.degrees ?? entity.source.degrees;
          if (!Number.isFinite(asked)) continue;

          next = updateEntity(next, {
            ...entity,
            source: { ...entity.source, degrees: action.reverse === true ? -asked : asked },
          });
          seeds.push(id);
        }
        break;

      case 'set-solid':
        for (const id of action.ids) {
          const entity = next.entities.get(id);
          // A mixed selection is normal; anything that is not a solid simply has no solid
          // parameters to change.
          if (entity?.source.op !== 'solid') continue;

          next = updateEntity(next, {
            ...entity,
            source: { ...entity.source, ...action.patch },
          });
          seeds.push(id);
        }
        break;

      case 'set-presentation':
        for (const id of action.ids) {
          const entity = next.entities.get(id);
          if (entity === undefined) continue;

          const updated: Entity = {
            ...entity,
            ...(action.visible === undefined ? {} : { visible: action.visible }),
            ...(action.locked === undefined ? {} : { locked: action.locked }),
            ...(action.zIndex === undefined ? {} : { zIndex: action.zIndex }),
            ...(action.isConstruction === undefined
              ? {}
              : { isConstruction: action.isConstruction }),
          };

          // A label is removed by dropping the property, not by setting it to undefined:
          // `exactOptionalPropertyTypes` makes those different types, and the serializer
          // relies on the distinction to round-trip losslessly.
          const withLabel =
            action.label === undefined
              ? updated
              : action.label === null
                ? stripLabel(updated)
                : { ...updated, label: action.label };

          next = updateEntity(next, withLabel);
          seeds.push(id);
        }
        break;
    }
  }

  return { document: next, seeds, removed };
}

/**
 * Parents of a deleted entity that nothing can reach any more.
 *
 * A construction can be built on a helper the user never made and never sees: a solid dropped
 * from the toolbar hangs off an invisible anchor, because the centre of a cube is not a point of
 * the figure and a dot drawn there reads as a mistake. Deletion cascades to *dependents*, so
 * removing the cube leaves that anchor behind — and an invisible entity with nothing depending on
 * it is unreachable in the strict sense: `hitTestAll` skips what is not drawn, so it cannot be
 * clicked, snapped to, or selected, and there is nothing left for it to hold up. It would sit in
 * the file forever, one per deleted solid.
 *
 * **All three conditions, and the middle one is the load-bearing one.** A point the *user* hid is
 * still theirs — it is in the object list, it can be shown again, and sweeping it away because the
 * last thing resting on it was deleted would be the editor destroying their work. An invisible
 * point and an invisible anchor are the same object from here; only `isConstruction` tells them
 * apart, and it is set by the tool that knows, never inferred. That is the difference between this
 * and the heuristic `isHelper` warns about: the flag is read, not guessed at.
 *
 * The ids go into `removed` with everything else, so undo restores them along with the solid —
 * one delete, one undo, and the figure comes back whole.
 */
function unreachableParentsOf(
  document: SceneDocument,
  deleted: Entity | undefined
): readonly EntityId[] {
  if (deleted === undefined) return [];

  return parentsOf(deleted.source).filter((id) => {
    const parent = document.entities.get(id);
    if (parent === undefined || parent.visible || !parent.isConstruction) return false;
    return dependentsOf(document, id).length === 0;
  });
}

/** True when applying these actions would change the document's structure. */
export function isStructural(actions: readonly EditAction[]): boolean {
  // `set-solid` is not cosmetic: it changes the geometry the solid evaluates to.
  const cosmetic = new Set(['update-entity', 'set-style', 'set-presentation']);
  return actions.some((action) => !cosmetic.has(action.kind));
}

/**
 * Removes the label property outright.
 *
 * Setting it to `undefined` is not the same thing under `exactOptionalPropertyTypes`, and
 * the serializer relies on the difference to round-trip losslessly.
 */
function stripLabel(entity: Entity): Entity {
  if (entity.label === undefined) return entity;
  const { label: _removed, ...rest } = entity;
  return rest;
}
